/**
 * PDP Subscription Widget Component
 * Provides subscription selector for products with selling plans on Product Detail Pages
 * Integrates with existing Eurus theme variant selector and cart system
 */

if (!window.Eurus.loadedScript.has('pdp-subscription-widget.js')) {
  window.Eurus.loadedScript.add('pdp-subscription-widget.js');

  const registerComponent = () => {
    if (typeof Alpine === 'undefined') {
      console.error('Alpine is not defined! Cannot register xPDPSubscriptionWidget');
      return;
    }

    /**
     * PDP Subscription Widget Alpine Component
     * @param {Object} config - Configuration object
     * @param {string} config.productId - Product ID
     * @param {string} config.sectionId - Section ID
     * @param {Array} config.variants - Product variants with selling plans
     * @param {number} config.discountPercentage - Subscription discount as decimal (e.g., 0.20 for 20%)
     * @param {boolean} config.subscriptionFirst - Whether subscription should be selected by default
     */
    Alpine.data('xPDPSubscriptionWidget', (config) => ({
      productId: config.productId,
      sectionId: config.sectionId,
      discountPercentage: config.discountPercentage,
      subscriptionFirst: config.subscriptionFirst || false,

      variants: [],
      variantsMap: {},

      currentVariantId: null,
      currentVariantPrice: 0,
      currentVariantSellingPlans: [],
      purchaseType: config.subscriptionFirst ? 'subscription' : 'onetime',
      selectedSellingPlan: '',
      hasSellingPlans: false,

      originalPrice: null,
      originalPriceVariantId: null,
      isInitialLoad: true,

      /**
       * Initialize component
       */
      init() {
        this.variants = this._parseJSON(config.variants);

        this._buildVariantsMap();

        this.hasSellingPlans = this.variants.some(v => v.selling_plan_allocations && v.selling_plan_allocations.length > 0);

        if (!this.hasSellingPlans) {
          return;
        }

        const variantInput = document.querySelector(`#update-variant-${this.sectionId}`);
        if (variantInput) {
          this.currentVariantId = parseInt(variantInput.value);
          this._updateCurrentVariantData();
        }

        this._listenForVariantChanges();

        this.$watch('purchaseType', (value) => {
          if (value === 'subscription') {
            if (!this.selectedSellingPlan && this.currentVariantSellingPlans.length > 0) {
              this.selectedSellingPlan = this.currentVariantSellingPlans[0].selling_plan_id;
            }
          }
          this._injectSellingPlanIntoForm();
          this._updateGlobalSellingPlanState();
          this._updatePriceDisplay();
        });

        this.$watch('selectedSellingPlan', () => {
          this._injectSellingPlanIntoForm();
          this._updateGlobalSellingPlanState();
        });
      },

      /**
       * Parse JSON string or return object as-is
       */
      _parseJSON(data) {
        if (typeof data === 'string') {
          try {
            return JSON.parse(data);
          } catch (e) {
            console.error('Failed to parse JSON:', e);
            return [];
          }
        }
        return data || [];
      },

      /**
       * Build variants map for O(1) lookup
       */
      _buildVariantsMap() {
        this.variants.forEach(variant => {
          this.variantsMap[variant.id] = variant;
        });
      },

      /**
       * Update current variant data based on currentVariantId
       */
      _updateCurrentVariantData() {
        const variant = this.variantsMap[this.currentVariantId];
        if (!variant) return;

        setTimeout(() => {
          const targetPriceEl = document.querySelector('.main-product-price .target-price');
          if (targetPriceEl) {
            const priceFromAttribute = parseInt(targetPriceEl.getAttribute('data-original-price'));
            if (!isNaN(priceFromAttribute) && priceFromAttribute > 0) {
              this.originalPrice = priceFromAttribute;
              this.originalPriceVariantId = this.currentVariantId;
              this.currentVariantPrice = priceFromAttribute;
            }
          }

          this._updatePriceDisplay();

          this.currentVariantSellingPlans = variant.selling_plan_allocations || [];

          if (this.isInitialLoad && this.subscriptionFirst && this.currentVariantSellingPlans.length > 0) {
            this.isInitialLoad = false;
            this.selectedSellingPlan = this.currentVariantSellingPlans[0].selling_plan_id;
            this._injectSellingPlanIntoForm();
            this._updateGlobalSellingPlanState();
            this._updatePriceDisplay();
          } else if (this.selectedSellingPlan) {
            const hasSelectedPlan = this.currentVariantSellingPlans.some(
              alloc => alloc.selling_plan_id == this.selectedSellingPlan
            );
            if (!hasSelectedPlan) {
              this.selectedSellingPlan = this.currentVariantSellingPlans[0]?.selling_plan_id || '';
              if (!this.selectedSellingPlan) {
                this.purchaseType = 'onetime';
              }
            }
          }
        }, 150);
      },

      /**
       * Listen for variant changes from the variant selector
       */
      _listenForVariantChanges() {
        const variantInput = document.querySelector(`#update-variant-${this.sectionId}`);
        if (variantInput) {
          const observer = new MutationObserver(() => {
            const newVariantId = parseInt(variantInput.value);
            if (newVariantId && newVariantId !== this.currentVariantId) {
              this.currentVariantId = newVariantId;
              this._updateCurrentVariantData();
            }
          });

          observer.observe(variantInput, {
            attributes: true,
            attributeFilter: ['value']
          });

          variantInput.addEventListener('change', () => {
            const newVariantId = parseInt(variantInput.value);
            if (newVariantId && newVariantId !== this.currentVariantId) {
              this.currentVariantId = newVariantId;
              this._updateCurrentVariantData();
            }
          });
        }

        document.addEventListener('eurus:variant:changed', (event) => {
          if (event.detail && event.detail.variant && event.detail.variant.id) {
            const newVariantId = parseInt(event.detail.variant.id);
            if (newVariantId && newVariantId !== this.currentVariantId) {
              this.currentVariantId = newVariantId;
              this._updateCurrentVariantData();
            }
          }
        });

        document.addEventListener('eurus:product-page-variant-select:updated', (event) => {
          setTimeout(() => {
            if (variantInput) {
              const newVariantId = parseInt(variantInput.value);
              if (newVariantId && newVariantId !== this.currentVariantId) {
                this.currentVariantId = newVariantId;
                this._updateCurrentVariantData();
              }
            }
          }, 200);
        });

        setInterval(() => {
          if (variantInput) {
            const newVariantId = parseInt(variantInput.value);
            if (newVariantId && newVariantId !== this.currentVariantId) {
              this.currentVariantId = newVariantId;
              this._updateCurrentVariantData();
            }
          }
        }, 500);
      },

      /**
       * Inject or remove selling plan input in the product form
       */
      _injectSellingPlanIntoForm() {
        const form = document.querySelector(`#product-form-${this.sectionId}`);
        if (!form) return;

        const existingInput = form.querySelector('input[name="selling_plan"]');
        if (existingInput) {
          existingInput.remove();
        }

        if (this.purchaseType === 'subscription' && this.selectedSellingPlan) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.value = this.selectedSellingPlan;
          form.appendChild(input);
        }
      },

      /**
       * Format price for display
       * @param {number} price - Price in cents
       * @returns {string} Formatted price string
       */
      formatPrice(price) {
        if (!price) return '';

        const currency = window.Shopify?.currency?.active || 'USD';

        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
          }).format(price / 100);
        } catch (e) {
          return `$${(price / 100).toFixed(2)}`;
        }
      },

      /**
       * Extract frequency from selling plan name
       * Converts "Delivery Every: 4 weeks" or "Delivered every 4 weeks" to just "4 weeks"
       * @param {string} planName - Full selling plan name
       * @returns {string} Just the frequency part
       */
      extractFrequency(planName) {
        if (!planName) return '';

        const patterns = [
          /^delivered?\s+every:?\s+/i,
          /^delivery\s+every:?\s+/i,
          /^subscribe\s+and\s+save:?\s+/i,
          /^subscription:?\s+/i,
          /^every:?\s+/i
        ];

        let frequency = planName;

        for (const pattern of patterns) {
          frequency = frequency.replace(pattern, '');
        }

        return frequency.trim();
      },

      /**
       * Update dropdown display (optional - for future enhancements)
       */
      updateDropdownDisplay(event) {
      },

      /**
       * Update global selling plan state (for sticky ATC and other components)
       * Mimics the window.selectedSellingPlan structure used by sticky-atc-recharge.js
       */
      _updateGlobalSellingPlanState() {
        if (!window.selectedSellingPlan) {
          window.selectedSellingPlan = {
            isSubscription: false,
            frequency: ''
          };
        }

        window.selectedSellingPlan.isSubscription = this.purchaseType === 'subscription';

        if (this.purchaseType === 'subscription' && this.selectedSellingPlan) {
          const selectedPlan = this.currentVariantSellingPlans.find(
            alloc => alloc.selling_plan_id == this.selectedSellingPlan
          );
          if (selectedPlan) {
            window.selectedSellingPlan.frequency = this.extractFrequency(selectedPlan.selling_plan_name);
          }
        } else {
          window.selectedSellingPlan.frequency = '';
        }

        if (typeof window.updateStickyATCWithSellingPlan === 'function') {
          window.updateStickyATCWithSellingPlan();
        }
      },

      /**
       * Update price display based on purchase type
       * Integrates with the existing sticky-atc-recharge.js price update system
       */
      _updatePriceDisplay() {
        if (this.originalPrice === null || this.originalPrice <= 0) {
          return;
        }

        let newPrice;
        if (this.purchaseType === 'subscription') {
          newPrice = Math.floor(this.originalPrice * (1 - this.discountPercentage));
        } else {
          newPrice = this.originalPrice;
        }

        const event = new CustomEvent('variant:changed', {
          detail: {
            price: newPrice,
            isSubscription: this.purchaseType === 'subscription'
          }
        });
        document.dispatchEvent(event);

        this._updateVisiblePrice(newPrice);

        this.currentVariantPrice = newPrice;
      },

      /**
       * Update visible price elements in the DOM
       * @param {number} priceInCents - Price in cents
       */
      _updateVisiblePrice(priceInCents) {
        const formattedPrice = this.formatPrice(priceInCents);

        const mainPriceContainer = document.querySelector('.main-product-price');
        if (mainPriceContainer) {
          const mainPriceElement = mainPriceContainer.querySelector('p.price > span:first-child, .price-sale');
          if (mainPriceElement) {
            mainPriceElement.textContent = formattedPrice;
          }
        }

        const stickyPriceContainers = document.querySelectorAll('[id^="price-sticky-"], .price-sticky');
        stickyPriceContainers.forEach(container => {
          const stickyPriceElement = container.querySelector('p.price > span:first-child, .price-sale, span:not(.stamped-io-rewards-points)');
          if (stickyPriceElement && !stickyPriceElement.closest('.stamped-io-rewards-points')) {
            stickyPriceElement.textContent = formattedPrice;
          }
        });
      }
    }));
  };

  document.addEventListener('alpine:init', () => {
    registerComponent();
  });

  setTimeout(() => {
    if (window.Alpine) {
      try {
        registerComponent();
      } catch (e) {
        // Already registered, ignore
      }
    }
  }, 100);
}
